import {FC} from "react"

import {buildCaseShareUrl} from "@frontend/hostContract"
import {trackEvent} from "@frontend/utils/trackEvent"

import {widgetChrome} from "./brandChrome"
import {WIDGET_HEADER_HEIGHT} from "./styles"

interface Props {
  displayMode?: "overlay" | "slideout"
  onMinimize: () => void
  onClose: () => void
  onNewConversation: () => void
  caseId: string | null
}

export const ChatPanelHeader: FC<Props> = ({
  displayMode = "overlay",
  onClose,
  onNewConversation,
  caseId,
}) => {
  const isSlideout = displayMode === "slideout"
  const headerTextColor = "#FFFFFF"
  const hasActiveCase = !!caseId
  const canShareCase =
    hasActiveCase &&
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function"

  const onShare = (): void => {
    const url = caseId ? buildCaseShareUrl(caseId) : window.location.href

    if (caseId) {
      trackEvent("widget_chat_header_share_click", caseId)
    }

    void navigator
      .share({
        url,
      })
      .catch(() => {})
  }

  return (
    <div
      style={{
        height: WIDGET_HEADER_HEIGHT,
        minHeight: WIDGET_HEADER_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        background: widgetChrome.headerGradient,
        borderTopLeftRadius: isSlideout ? 0 : 16,
        borderTopRightRadius: isSlideout ? 0 : 16,
        borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
        color: headerTextColor,
        userSelect: "none",
      }}
    >
      <div style={{display: "flex", alignItems: "center", gap: 10}}>
        {/* Sparkle / AI icon */}
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M11.999 1.83203C12.7991 4.068 14.222 6.21072 15.9922 7.98828C17.7608 9.76426 19.8989 11.1971 22.1484 11.999C19.8988 12.8009 17.7608 14.2347 15.9922 16.0107C14.2219 17.7885 12.7992 19.932 11.999 22.168C11.1988 19.9323 9.77768 17.7892 8.00781 16.0117C6.23917 14.2355 4.10123 12.8011 1.85156 11.999C4.10107 11.1971 6.2393 9.76426 8.00781 7.98828C9.77771 6.21088 11.1988 4.06773 11.999 1.83203Z"
            fill={widgetChrome.starFill}
            stroke="url(#repairGuruStarGradient)"
            strokeMiterlimit="7.1853"
          />
          <defs>
            <linearGradient
              id="repairGuruStarGradient"
              x1="-21.75"
              y1="0"
              x2="46.5"
              y2="1.15988e-08"
              gradientUnits="userSpaceOnUse"
            >
              {widgetChrome.starGradientStops.map((stop: {offset?: string; color: string}, i: number) => (
                <stop
                  key={i}
                  {...(stop.offset ? {offset: stop.offset} : {})}
                  stopColor={stop.color}
                />
              ))}
            </linearGradient>
          </defs>
        </svg>
        <span
          style={{
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "#FFFFFF",
          }}
        >
          {widgetChrome.productTitle}
        </span>
      </div>
      <div style={{display: "flex", alignItems: "center", gap: 4}}>
        {canShareCase && (
          <button
            onClick={onShare}
            aria-label="Share conversation"
            title="Share conversation"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              opacity: 0.8,
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.8"
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="18"
                cy="5"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
              />
              <circle
                cx="6"
                cy="12"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
              />
              <circle
                cx="18"
                cy="19"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
              />
              <line
                x1="8.59"
                x2="15.42"
                y1="13.51"
                y2="17.49"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="15.41"
                x2="8.59"
                y1="6.51"
                y2="10.49"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {hasActiveCase && (
          <button
            onClick={onNewConversation}
            aria-label="New conversation"
            title="New conversation"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              opacity: 0.8,
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.8"
            }}
          >
            {/* Plus / new icon */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {/* Close (X) */}
        <button
          onClick={onClose}
          aria-label="Close chat"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 6,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            opacity: 0.8,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.8"
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
